import React, { CSSProperties } from "react";
import { Theme } from "../../../../styling/baseui_theme";
import { Card, CardProps, CardOverrides, StyledBody } from "baseui/card";
import { mergeOverrides } from "baseui";
import { Overrides } from "baseui/overrides";
import styled from "styled-components";
import { InformationFilled } from "@carbon/icons-react";
import { CarbonIconProps } from "@carbon/icons-react/lib/CarbonIcon";
import { Tooltip } from "../tooltip";

export enum ORIENTATION {
  horizontal = "horizontal",
  vertical = "vertical",
}

export const StyledHorizontalRule = styled.hr`
  background-color: ${({ theme }) => theme.colors.borderOpaque};
  height: 1px;
  border: none;
  width: 100%;
  margin-top: ${({ theme }) => theme.sizing.scale800};
  margin-bottom: ${({ theme }) => theme.sizing.scale800};
`;

const StyledHorizontalSpace = styled.div`
  width: 100%;
  margin-top: ${({ theme }) => theme.sizing.scale600};
  margin-bottom: ${({ theme }) => theme.sizing.scale600};
`;

const StyledHorizontalDiv = styled.div`
  width: 100%;
  display: flex;
`;

const StyledLeftDiv = styled.div`
  flex: 1;
  padding-right: ${({ theme }) => theme.sizing.scale800};
  vertical-align: middle;
`;

const StyledRightDiv = styled.div`
  flex: 1;
  border-left: ${({ theme }) => theme.colors.borderOpaque} solid 1px;
  padding-left: ${({ theme }) => theme.sizing.scale800};
`;

// A little hacky but necessary for focusability
const StyledBaseUISectionBlock = styled(Card)<SectionBlockProps & { tabIndex?: string }>`
  &:focus,
  &:focus-visible {
    outline-color: ${({ theme }) => theme.colors.accent};
  }
`;

const sectionBlockStyleOverride: Overrides<CardOverrides> = {
  Root: {
    style: ({ $theme }: { $theme: Theme }) => ({
      backgroundColor: $theme.colors.white,
      borderStyle: "none",
      borderRadius: $theme.borders.radius500,
      boxShadow: $theme.lighting.shadow400,
      paddingTop: $theme.sizing.scale600,
      paddingBottom: $theme.sizing.scale900,
    }),
  },
  Contents: {
    style: () => ({
      position: "relative",
      marginTop: "0px",
      marginBottom: "0px",
      marginLeft: "0px",
      marginRight: "0px",
    }),
  },
  Title: {
    style: ({ $theme }: { $theme: Theme }) => ({
      fontSize: $theme.typography.HeadingXSmall.fontSize,
      lineHeight: $theme.typography.HeadingXSmall.lineHeight,
      fontWeight: $theme.typography.HeadingXSmall.fontWeight,
      paddingLeft: $theme.sizing.scale800,
      paddingRight: $theme.sizing.scale800,
      paddingBottom: $theme.sizing.scale600,
      borderBottom: `${$theme.colors.borderOpaque} solid 1px`,
      verticalAlign: "middle",
    }),
  },
  Body: {
    style: ({ $theme }: { $theme: Theme }) => ({
      marginTop: $theme.sizing.scale800,
      marginLeft: $theme.sizing.scale800,
      marginRight: $theme.sizing.scale800,
      marginBottom: 0,
    }),
  },
};

// ======= Component Types =======
export type SectionBlockProps = Omit<CardProps, "overrides"> & {
  // Title props
  title?: React.ReactNode;
  titleTooltip?: string | undefined;
  titleTooltipSize?: CarbonIconProps["size"] | undefined;

  // Layout props (supports both old and new API)
  layout?: ORIENTATION[keyof ORIENTATION];

  // Content props (new API)
  sections?: React.ReactNode[];

  // Footer props
  button?: React.ReactNode;
  footer?: React.ReactNode;

  // Style props
  className?: string | undefined;
  styletronOverrides?: CardOverrides;
  bodyOverrides?: CSSProperties;

  // Other props
  ariaLabel?: string;
  showFooterHorizontalRule?: boolean;
};

// Title component with optional tooltip
const TitleWithTooltip = ({
  title,
  tooltip,
  tooltipSize = 16,
}: {
  title: React.ReactNode;
  tooltip?: string;
  tooltipSize?: CarbonIconProps["size"];
}) => {
  if (!tooltip) return <>{title}</>;

  return (
    <>
      {title}{" "}
      <Tooltip content={tooltip}>
        <InformationFilled size={tooltipSize} className="info-tooltip" />
      </Tooltip>
    </>
  );
};

// Vertical layout component
const VerticalLayout = ({ children }: { children: React.ReactNode[] }) => {
  const filteredChildren = children.filter(Boolean);

  return (
    <>
      {filteredChildren.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {index < filteredChildren.length - 1 && <StyledHorizontalRule />}
        </React.Fragment>
      ))}
    </>
  );
};

// Horizontal layout component
const HorizontalLayout = ({ children }: { children: React.ReactNode[] }) => {
  const filteredChildren = children.filter(Boolean);

  return (
    <StyledHorizontalDiv>
      {filteredChildren.map((child, index) =>
        index === 0 ? (
          <StyledLeftDiv key={index}>{child}</StyledLeftDiv>
        ) : (
          <StyledRightDiv key={index}>{child}</StyledRightDiv>
        ),
      )}
    </StyledHorizontalDiv>
  );
};

// Footer component
const Footer = ({
  hasBodyContent,
  showDivider,
  footer,
  button,
}: {
  hasBodyContent: boolean;
  showDivider?: boolean;
  footer?: React.ReactNode;
  button?: React.ReactNode;
}) => {
  // Check if there's actual footer content to display
  const hasFooterContent = !!(footer || button);

  const showHorizontalRule = hasBodyContent && hasFooterContent && showDivider;
  const showHorizontalSpacing = hasBodyContent && hasFooterContent && !showDivider;

  if (!hasFooterContent) return null;

  const divider = showHorizontalRule ? (
    <StyledHorizontalRule />
  ) : showHorizontalSpacing ? (
    <StyledHorizontalSpace />
  ) : null;

  return (
    <>
      {divider}
      {button ? (
        <>
          {footer}
          {button}
        </>
      ) : (
        footer
      )}
    </>
  );
};

// ======= Main Component =======
export const SectionBlock = ({
  title,
  titleTooltip,
  titleTooltipSize = 16,
  layout = ORIENTATION.vertical,
  sections = [],
  button,
  footer,
  showFooterHorizontalRule = true,
  className,
  styletronOverrides,
  bodyOverrides,
  children,
  ariaLabel,
  ...props
}: SectionBlockProps) => {
  let styleOverride = sectionBlockStyleOverride;

  if (styletronOverrides) {
    styleOverride = mergeOverrides(
      sectionBlockStyleOverride,
      styletronOverrides as Overrides<CardOverrides>,
    );
  }

  if (!title) {
    styleOverride = mergeOverrides(styleOverride, {
      Body: {
        style: () => ({
          marginTop: 0,
        }),
      },
    } as Overrides<CardOverrides>);
  }

  const effectiveSections = sections.filter(Boolean);

  // Check if there's any content in the body for footer divider logic
  const hasBodyContent = !!(children || effectiveSections.length > 0);

  // Prepare the body content - prioritize direct children over sections
  let fullBody = children;

  if (!children && effectiveSections.length > 0) {
    // Choose layout based on orientation
    const isHorizontal = layout === ORIENTATION.horizontal;

    fullBody = isHorizontal ? (
      <HorizontalLayout>{effectiveSections}</HorizontalLayout>
    ) : (
      <VerticalLayout>{effectiveSections}</VerticalLayout>
    );
  }

  return (
    <StyledBaseUISectionBlock
      title={
        title && (
          <TitleWithTooltip title={title} tooltip={titleTooltip} tooltipSize={titleTooltipSize} />
        )
      }
      {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
      {...(className ? { className } : {})}
      {...props}
      overrides={styleOverride}
      tabIndex="0"
    >
      <StyledBody style={bodyOverrides || { marginBottom: 0 }} id="base-styled-body">
        {fullBody}
      </StyledBody>
      <Footer
        hasBodyContent={hasBodyContent}
        showDivider={showFooterHorizontalRule}
        footer={footer}
        button={button}
      />
    </StyledBaseUISectionBlock>
  );
};

// Maintained for backward compatibility
export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: start;
  flex-direction: column;

  // add margin to the last child
  & > *:nth-child(n + 2):last-child {
    margin-top: ${({ theme }) => theme.sizing.scale600};

    ${({ theme }) => theme.mediaQuery.medium} {
      margin-left: ${({ theme }) => theme.sizing.scale600};
      margin-top: 0;
    }
  }

  ${({ theme }) => theme.mediaQuery.medium} {
    flex-direction: row;
  }
`;
